import { addDaysToDate, removeTimeFromDate } from "../helper/Parser";
import Big from "big.js";
export var FilterCondition;
(function (FilterCondition) {
    FilterCondition["EQ"] = "EQ";
    FilterCondition["LE"] = "LE";
    FilterCondition["L"] = "L";
    FilterCondition["GE"] = "GE";
    FilterCondition["G"] = "G";
    FilterCondition["N"] = "N";
    //Now special filter types for arrays / tags
    FilterCondition["CO"] = "CO";
})(FilterCondition || (FilterCondition = {}));
export var FilterType;
(function (FilterType) {
    FilterType["AND"] = "AND";
    FilterType["OR"] = "OR";
})(FilterType || (FilterType = {}));
export var SortingOrder;
(function (SortingOrder) {
    SortingOrder["ASC"] = "ASC";
    SortingOrder["DESC"] = "DESC";
})(SortingOrder || (SortingOrder = {}));
/**
 * Create a filter value used by the api
 * @param value
 * @param filterCondition
 */
export function buildFilterValue(value, filterCondition, filterType) {
    let usedValue = value;
    if (Array.isArray(value)) {
        const from = value[0];
        const until = value[1];
        usedValue = (from ? from.toISOString() : "null") + ";" + (until ? until.toISOString() : "null");
    }
    return usedValue + "|" + filterCondition.toString() + "|" + (filterType?.toString() || FilterType.AND.toString());
}
export var ViewMode;
(function (ViewMode) {
    ViewMode["LIST"] = "LIST";
    ViewMode["SMALL"] = "SMALL";
    ViewMode["BIG"] = "BIG";
})(ViewMode || (ViewMode = {}));
export function createSimpleListConfiguration(creator) {
    return {
        header: creator.header || null,
        growHeight: creator.growHeight != undefined ? creator.growHeight : true,
        singleSelectToggle: creator.singleSelectToggle != undefined ? creator.singleSelectToggle : false,
        showEmptyAdd: creator.showEmptyAdd != undefined ? creator.showEmptyAdd : true,
        viewMode: creator.viewMode || ViewMode.SMALL,
        showViewModeSwitches: creator.showViewModeSwitches || [],
        allowMultiSelection: creator.allowMultiSelection != undefined ? creator.allowMultiSelection : true,
        tableModeConfig: {
            ...{
                tableHeaderOffset: 0,
                showTableConfig: false,
            }, ...creator.tableModeConfig
        },
        cardModeConfig: {
            ...{
                amountOfCols: 1,
                gapSize: 6,
            }, ...creator.cardModeConfig
        }
    };
}
export function createSimpleListListFilter(simpleListListFilter = {}) {
    const filter = {
        ...{
            localStorageKey: null,
            page: 0,
            size: 25,
            gridCols: 4,
            searchType: FilterType.AND,
            sortingOrder: SortingOrder.ASC,
            sortingKey: null,
            showFilterIcon: true,
            filterOptions: [],
            deletedCounter: 0,
            orInputValue: '',
            isFilterSaveAllowed: true
        }, ...simpleListListFilter
    };
    //Set the inital sorting key
    let activeSorting = simpleListListFilter?.filterOptions?.find((opt) => opt.isSorting == null || opt.isSorting) || null;
    if (activeSorting) {
        filter.sortingKey = activeSorting.key;
    }
    //Load the settings from local storage
    loadSettingsFromLocalStorage(filter);
    return filter;
}
/**
 * Save the settings the user made to this list to local storage.
 */
export function saveListSettingsToLocalStorage(listFilter) {
    //Ignore null / undefined values
    if (!listFilter.localStorageKey)
        return;
    const headerVisibility = [];
    const filterValues = [];
    listFilter.filterOptions.forEach((filter) => {
        //Build the header information
        headerVisibility.push({
            key: filter.key,
            showAsTableHeader: filter.tableOption?.showAsTableHeader != null
                ? filter.tableOption?.showAsTableHeader
                : true,
            width: filter.tableOption?.width
        });
        if (listFilter.isFilterSaveAllowed) {
            //Save the filter value as well
            if (filter.key != null && (filter.value != null || filter.value != filter.defaultValue) && !filter.resetProtect) {
                filterValues.push({ key: filter.key, value: filter.value });
            }
        }
    });
    //Build the object we will save to the local storage.
    const settingToSave = {
        sortingKey: listFilter.sortingKey,
        sortingOrder: listFilter.sortingOrder,
        //Create the mapping we need to restore the headers later on.
        headerVisibility: headerVisibility,
        filterValues: filterValues,
        searchBarInput: listFilter.isFilterSaveAllowed ? listFilter.searchBarInput : undefined
    };
    //Save the filter to local storage
    window.localStorage.setItem(`${listFilter.localStorageKey}-filter`, JSON.stringify(settingToSave));
}
/**
 * Remove all saved values for filters.
 * @param listFilter
 */
export function clearSavedFilterValues(listFilter) {
    //Remove all the values which are not reset protected
    listFilter.filterOptions.forEach(filter => {
        let writeProtected = filter.resetProtect != null ? filter.resetProtect : false;
        //Clear the value
        if (!writeProtected || filter.defaultValue) {
            if (filter.defaultValue != undefined) {
                filter.value = filter.defaultValue;
            }
            else {
                filter.value = null;
            }
        }
    });
    //Clear search bar property as well
    listFilter.searchBarInput = undefined;
}
/**
 * Load the settings the user made to this list from local storage and
 * set it back to the current filter object.
 */
function loadSettingsFromLocalStorage(listFilter) {
    //Ignore null / undefined keys
    if (!listFilter.localStorageKey)
        return;
    //Get the memory from local storage
    const loadedSortingKey = window.localStorage.getItem(`${listFilter.localStorageKey}-filter`);
    if (loadedSortingKey) {
        try {
            //Load the information from storage.
            let loadedConfig = JSON.parse(loadedSortingKey);
            //Set the keys back to the in memory object.
            listFilter.sortingKey = loadedConfig.sortingKey || null;
            listFilter.sortingOrder =
                SortingOrder[loadedConfig.sortingOrder] || SortingOrder.ASC;
            //Reset the header visibility back to the in memory object.
            loadedConfig.headerVisibility.forEach((conf) => {
                const targetFilter = listFilter.filterOptions.find((filter) => filter.key == conf.key);
                //Only restore options that actually exist and are visible
                if (targetFilter) {
                    //Set the visibility from the loaded config only if the current config allows to change it.
                    if (targetFilter.tableOption?.allowToggleVisibility == true) {
                        targetFilter.tableOption = {
                            ...targetFilter.tableOption,
                            showAsTableHeader: conf.showAsTableHeader,
                        };
                    }
                    //Merge all necessary properties
                    targetFilter.tableOption = {
                        ...targetFilter.tableOption,
                        width: conf.width
                    };
                }
            });
            //Do not load the filter values if prohibited
            if (listFilter.isFilterSaveAllowed) {
                //Restore the saved values to the filter
                loadedConfig.filterValues.forEach((memmory) => {
                    //Get the target filter
                    const targetFilter = listFilter.filterOptions.find((filter) => filter.key == memmory.key && !filter.resetProtect);
                    if (targetFilter) {
                        //Restore the value.
                        targetFilter.value = memmory.value;
                    }
                });
                //Restore the search bar input
                listFilter.searchBarInput = loadedConfig.searchBarInput;
            }
        }
        catch {
            //In case of failure remove the saved object.
            window.localStorage.removeItem(`${listFilter.localStorageKey}-filter`);
        }
    }
}
/**
 * Create the css grid string use for the table to display the cols.
 * @param tableFilter
 * @returns
 */
export function generateTableGrid(tableFilter) {
    let gridVal = "display: grid; grid-template-columns:";
    tableFilter.filterOptions.forEach((header) => {
        if (header.tableOption?.showAsTableHeader == false) {
            //Do nothing
        }
        else if ((header.tableOption?.width || -1) == -1) {
            gridVal += " minMax(20px, 1fr)";
        }
        else {
            gridVal += " " + (header.tableOption?.width || 100) + "px";
        }
    });
    return gridVal + ";";
}
/**
 * Get the correct boolean for a deafult filter value which must be localy combined.
 * @param filterValue
 * @param targetNumber
 * @returns
 */
export function getLocalFilterValueForNumber(filterValue, targetNumber) {
    let nrFilter = true;
    if (filterValue) {
        if (!targetNumber)
            return false;
        let values = filterValue.split("|");
        let filterType = values[1];
        try {
            let nr = new Big(values[0]);
            switch (filterType) {
                case "EQ": {
                    nrFilter = nr.cmp(targetNumber) == 0;
                    break;
                }
                case "GE": {
                    nrFilter = nr.lte(targetNumber);
                    break;
                }
                case "G": {
                    nrFilter = nr.lt(targetNumber);
                    break;
                }
                case "LE": {
                    nrFilter = nr.gte(targetNumber);
                    break;
                }
                case "L": {
                    nrFilter = nr.gt(targetNumber);
                    break;
                }
            }
        }
        catch (err) {
            //If it's not a number return false, so that it does not counts as match
            return false;
        }
    }
    return nrFilter;
}
/**
 * Get the right boolean for a local string filter.
 * @param filterValue
 * @param targetString
 * @returns
 */
export function getLocalFilterValueForString(filterValue, targetString) {
    if (filterValue) {
        if (!targetString)
            return false;
        let val = filterValue.split("|")[0] || "";
        return targetString.toLowerCase().includes(val.toLowerCase());
    }
    return true;
}
/**
 * Get the right boolean for a local filter with two date values.
 * @param filterValue
 * @param targetString
 * @returns
 */
export function getLocalFilterValueForDate(filterValue, targetString) {
    let resultValue = true;
    if (filterValue) {
        if (!targetString)
            return false;
        //Split the values
        let values = filterValue.split("|");
        //We have a date range
        let dateRanges = values[0].split(";");
        //We need to extract the date value and remove the time component from them. For the up to we have to kill time and add one day.
        let filterDateFrom = dateRanges[0] != "null" ? removeTimeFromDate(new Date(dateRanges[0])) : null;
        let filterDateUpto = dateRanges[1] != "null" ? addDaysToDate(new Date(dateRanges[1]), 1) : null;
        //Parse the target value as well
        let targetDate = new Date(targetString);
        if (filterDateFrom != null && filterDateUpto != null) {
            //For upto compare we have to take less because its at 00:00:00 on the next day
            resultValue = targetDate.getTime() >= filterDateFrom.getTime() && targetDate.getTime() < filterDateUpto.getTime();
        }
        else if (filterDateFrom != null && filterDateUpto == null) {
            resultValue = targetDate.getTime() >= filterDateFrom.getTime();
        }
        else if (filterDateFrom == null && filterDateUpto != null) {
            //For upto compare we have to take less because its at 00:00:00 on the next day
            resultValue = targetDate.getTime() < filterDateUpto.getTime();
        }
    }
    return resultValue;
}
